// src/stores/cartStore.tsx
"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useSessionStore } from "@/stores/sessionStore";

interface CartItem {
  id: number;
  media_id: number;
  project_id?: string;
  quantity: number;
  media: {
    display_name: string;
    market_price: number;
    url: string;
    hyperlink: string;
    img_final: string;
  };
  project?: { name: string };
}

interface Promo {
  id?: number;
  code: string;
  discount_type: string;
  discount_value: number;
  discount_applied: number;
  success_message: string;
  limit_products?: string;
  note?: string;
  max_amount?: number;
}

interface CartResponse {
  items: CartItem[];
  promo: Promo | null;
  subtotal: number;
  discount: number;
  total: number;
}

interface CartState {
  guestCart: {
    cartItems: CartItem[];
    promo: Promo | null;
    subtotal: number;
    total: number;
  };
  loggedInCart: {
    cartItems: CartItem[];
    promo: Promo | null;
    subtotal: number;
    discount: number;
    total: number;
  };
  selectedProject: string | null;
  promoCode: string;
  promoError: string | null;
  isApplying: boolean;
  fetchCart: (userId?: string) => Promise<void>;
  addToCart: (item: Omit<CartItem, "id">) => Promise<void>;
  removeFromCart: (cartItemId: number) => Promise<string | null>;
  applyPromoCode: (code: string) => Promise<void>;
  removePromoCode: () => Promise<void>;
  setSelectedProject: (projectId: string | null) => void;
  setPromoCode: (code: string) => void;
  setPromoError: (error: string | null) => void;
  setIsApplying: (isApplying: boolean) => void;
  clearCart: (isAuthenticated: boolean) => void;
}

// Helper function to calculate discount based on cart items and promo
const calculateDiscount = (cartItems: CartItem[], promo: Promo | null): number => {
  if (!promo) return 0;

  const { discount_type, discount_value, limit_products, max_amount } = promo;
  const limitedProducts = limit_products ? limit_products.split(",").map(Number) : null;

  // Calculate eligible subtotal
  const eligible_subtotal = cartItems.reduce((sum, item) => {
    const isEligible = limitedProducts ? limitedProducts.includes(item.media_id) : true;
    return isEligible ? sum + item.media.market_price * item.quantity : sum;
  }, 0);

  let discount = 0;
  if (discount_type === "percent") {
    discount = eligible_subtotal * (discount_value / 100);
    if (max_amount) {
      discount = Math.min(discount, max_amount);
    }
  } else if (discount_type === "fixed") {
    discount = Math.min(discount_value, eligible_subtotal);
  }

  return discount;
};

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      guestCart: {
        cartItems: [],
        promo: null,
        subtotal: 0,
        total: 0,
      },
      loggedInCart: {
        cartItems: [],
        promo: null,
        subtotal: 0,
        discount: 0,
        total: 0,
      },
      selectedProject: null,
      promoCode: "",
      promoError: null,
      isApplying: false,

      fetchCart: async (userId?: string) => {
        const session = useSessionStore.getState().session;
        const effectiveUserId = userId || session?.user?.id?.toString();
        const token = session?.user?.token;

        if (effectiveUserId && token) {
          try {
            const apiUrl = process.env.NEXT_PUBLIC_API_URL;
            if (!apiUrl) {
              throw new Error("NEXT_PUBLIC_API_URL is not defined in environment variables");
            }
            const response = await fetch(`${apiUrl}/cart`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const data: CartResponse = await response.json();
            if (response.ok) {
              set({
                loggedInCart: {
                  cartItems: Array.isArray(data.items) ? data.items : [],
                  promo: data.promo || null,
                  subtotal: data.subtotal || 0,
                  discount: data.discount || 0,
                  total: data.total || 0,
                },
                promoCode: data.promo ? data.promo.code : "",
                promoError: null,
              });
              console.log("Server cart fetched:", data.items);
            } else {
              throw new Error("Failed to fetch cart");
            }
          } catch (error) {
            console.error("Error fetching cart:", error);
            set({ promoError: "Failed to fetch cart from server" });
          }
        } else {
          console.log("Guest cart loaded from persisted state");
        }
      },

      addToCart: async (item) => {
        const session = useSessionStore.getState().session;
        if (session?.user?.id && session?.user?.token) {
          const token = session.user.token;
          try {
            const apiUrl = process.env.NEXT_PUBLIC_API_URL;
            if (!apiUrl) {
              throw new Error("NEXT_PUBLIC_API_URL is not defined in environment variables");
            }
            await fetch(`${apiUrl}/cart/add`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(item),
            });
            await get().fetchCart(session.user.id.toString());
          } catch (error) {
            console.error("Error adding to cart:", error);
            set({ promoError: "Failed to add item to cart" });
          }
        } else {
          set((state) => {
            const newItem = { ...item, id: Date.now() };
            const updatedItems = [...state.guestCart.cartItems, newItem];
            const subtotal = updatedItems.reduce(
              (sum, i) => sum + i.media.market_price * i.quantity,
              0
            );
            const discount = calculateDiscount(updatedItems, state.guestCart.promo);
            const updatedPromo = state.guestCart.promo
              ? { ...state.guestCart.promo, discount_applied: discount }
              : null;
            return {
              guestCart: {
                cartItems: updatedItems,
                promo: updatedPromo,
                subtotal,
                total: subtotal - discount,
              },
            };
          });
        }
      },

      removeFromCart: async (cartItemId: number) => {
        const session = useSessionStore.getState().session;
        if (session?.user?.id && session?.user?.token) {
          const token = session.user.token;
          try {
            const apiUrl = process.env.NEXT_PUBLIC_API_URL;
            if (!apiUrl) {
              throw new Error("NEXT_PUBLIC_API_URL is not defined in environment variables");
            }
            const response = await fetch(
              `${apiUrl}/cart/update?cart_item_id=${cartItemId}&quantity=0`,
              {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            if (response.ok) {
              await get().fetchCart(session.user.id.toString());
              return null;
            } else {
              const data = await response.json();
              throw new Error(data.message || "Failed to remove from cart");
            }
          } catch (error) {
            console.error("Error removing from cart:", error);
            set({ promoError: "Failed to remove item from cart" });
            return null;
          }
        } else {
          set((state) => {
            const updatedItems = state.guestCart.cartItems.filter(
              (item) => item.id !== cartItemId
            );
            const subtotal = updatedItems.reduce(
              (sum, i) => sum + i.media.market_price * i.quantity,
              0
            );
            const discount = calculateDiscount(updatedItems, state.guestCart.promo);
            const updatedPromo = state.guestCart.promo
              ? { ...state.guestCart.promo, discount_applied: discount }
              : null;
            return {
              guestCart: {
                cartItems: updatedItems,
                promo: updatedPromo,
                subtotal,
                total: subtotal - discount,
              },
            };
          });
          return null;
        }
      },

      applyPromoCode: async (code: string) => {
        const session = useSessionStore.getState().session;
        set({ isApplying: true, promoError: null });

        try {
          if (session?.user?.id && session?.user?.token) {
            const token = session.user.token;
            const apiUrl = process.env.NEXT_PUBLIC_API_URL;
            if (!apiUrl) {
              throw new Error("NEXT_PUBLIC_API_URL is not defined in environment variables");
            }
            const response = await fetch(
              `${apiUrl}/cart/update-promo?promo_code=${encodeURIComponent(code)}`,
              {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            if (response.ok) {
              await get().fetchCart(session.user.id.toString());
            } else {
              const data = await response.json();
              throw new Error(data.message || "Promo code is no longer valid");
            }
          } else {
            const apiUrl = process.env.NEXT_PUBLIC_API_URL;
            if (!apiUrl) {
              throw new Error("NEXT_PUBLIC_API_URL is not defined in environment variables");
            }
            const response = await fetch(`${apiUrl}/promo/validate`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ code }),
            });
            if (response.ok) {
              const promoData = await response.json();
              set((state) => {
                const subtotal = state.guestCart.subtotal;
                const discountApplied = calculateDiscount(state.guestCart.cartItems, {
                  code,
                  discount_type: promoData.discount_type,
                  discount_value: promoData.discount_value,
                  discount_applied: 0,
                  success_message: promoData.success_message || "Promo code applied successfully",
                  limit_products: promoData.limit_products || undefined,
                  note: promoData.note || undefined,
                  max_amount: promoData.max_amount || undefined,
                });
                const promo: Promo = {
                  id: promoData.id || undefined,
                  code,
                  discount_type: promoData.discount_type,
                  discount_value: promoData.discount_value,
                  discount_applied: discountApplied,
                  success_message: promoData.success_message || "Promo code applied successfully",
                  limit_products: promoData.limit_products || undefined,
                  note: promoData.note || undefined,
                  max_amount: promoData.max_amount || undefined,
                };
                // Ensure total is non-negative
                const total = Math.max(0, subtotal - discountApplied);
                return {
                  guestCart: {
                    ...state.guestCart,
                    promo,
                    total,
                  },
                  promoCode: code,
                };
              });
            } else {
              const data = await response.json();
              throw new Error(data.detail || "Invalid promo code");
            }
          }
        } catch (error) {
          console.error("Error applying promo code:", error);
          set({ promoError: error instanceof Error ? error.message : "Failed to apply promo code" });
          throw error;
        } finally {
          set({ isApplying: false });
        }
      },

      removePromoCode: async () => {
        const session = useSessionStore.getState().session;
        set({ promoError: null });

        if (session?.user?.id && session?.user?.token) {
          try {
            const token = session.user.token;
            const apiUrl = process.env.NEXT_PUBLIC_API_URL;
            if (!apiUrl) {
              throw new Error("NEXT_PUBLIC_API_URL is not defined in environment variables");
            }
            const response = await fetch(`${apiUrl}/cart/update-promo`, {
              method: "PUT",
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
              await get().fetchCart(session.user.id.toString());
            } else {
              throw new Error("Failed to remove promo code");
            }
          } catch (error) {
            console.error("Error removing promo code:", error);
            set({ promoError: "Failed to remove promo code" });
            throw error;
          }
        } else {
          set((state) => ({
            guestCart: {
              ...state.guestCart,
              promo: null,
              total: state.guestCart.subtotal,
            },
            promoCode: "",
          }));
        }
      },

      setSelectedProject: (projectId) => set({ selectedProject: projectId }),
      setPromoCode: (code) => set({ promoCode: code }),
      setPromoError: (error) => set({ promoError: error }),
      setIsApplying: (isApplying) => set({ isApplying }),

      clearCart: (isAuthenticated) =>
        set((state) =>
          isAuthenticated
            ? {
                loggedInCart: {
                  cartItems: [],
                  promo: null,
                  subtotal: 0,
                  discount: 0,
                  total: 0,
                },
                promoCode: "",
                promoError: null,
              }
            : {
                guestCart: {
                  cartItems: [],
                  promo: null,
                  subtotal: 0,
                  total: 0,
                },
              }
        ),
    }),
    {
      name: "guest-cart-storage",
      partialize: (state) => ({
        guestCart: state.guestCart,
      }),
    }
  )
);